import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { User, Phone, Mail, Lock, Eye, EyeOff, Loader2, ArrowLeft, Heart, MessageCircle, ShoppingBag } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CustomerAuth() {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [storeName, setStoreName] = useState("");

  const [firstName, setFirstName] = useState("");
  const [contact, setContact] = useState("");
  const [contactType, setContactType] = useState("email");
  const [password, setPassword] = useState("");

  useEffect(() => {
    const fetchStore = async () => {
      try {
        const response = await axios.get(`${API}/api/public/vitrine/${userId}`);
        setStoreName(response.data.store_name || "");
      } catch (error) {
        console.error("Failed to fetch store name");
      }
    };
    fetchStore();
  }, [userId]);

  useEffect(() => {
    const link = document.createElement("link");
    link.href = "https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let response;
      if (mode === "register") {
        response = await axios.post(`${API}/api/customer/register`, {
          first_name: firstName,
          contact,
          contact_type: contactType,
          password,
          user_id: userId,
        });
        toast.success("Bienvenue chez nous !");
      } else {
        response = await axios.post(`${API}/api/customer/login`, {
          contact,
          password,
          user_id: userId,
        });
        toast.success("Connexion réussie !");
      }
      localStorage.setItem(`customer_token_${userId}`, response.data.access_token);
      navigate(`/vitrine/${userId}/espace/favorites`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Une erreur s'est produite");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ca-root" data-testid="customer-auth-page">
      {/* Back link */}
      <Link to={`/vitrine/${userId}`} className="ca-back" data-testid="back-to-store">
        <ArrowLeft size={15} /> Retour à la boutique
      </Link>

      <div className="ca-wrapper">
        <div className="ca-card">
          {/* Top accent line */}
          <div className="ca-accent" />

          {/* Icon */}
          <div className="ca-icon-wrap">
            <Heart size={22} />
          </div>

          {/* Title */}
          <h1 className="ca-title">Mon Espace</h1>
          {storeName && <p className="ca-subtitle">{storeName}</p>}

          {/* Mode toggle */}
          <div className="ca-toggle" data-testid="auth-mode-toggle">
            <button
              type="button"
              onClick={() => setMode("login")}
              className={`ca-toggle-btn ${mode === "login" ? "active" : ""}`}
              data-testid="toggle-login"
            >
              Se connecter
            </button>
            <button
              type="button"
              onClick={() => setMode("register")}
              className={`ca-toggle-btn ${mode === "register" ? "active" : ""}`}
              data-testid="toggle-register"
            >
              Créer un compte
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="ca-form">
            {/* First name (register only) */}
            {mode === "register" && (
              <div className="ca-field">
                <label className="ca-label">Prénom</label>
                <div className="ca-input-wrap">
                  <User size={15} className="ca-input-icon" />
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Marie"
                    className="ca-input"
                    required
                    data-testid="customer-firstname"
                  />
                </div>
              </div>
            )}

            {/* Contact type (register only) */}
            {mode === "register" && (
              <div className="ca-field">
                <label className="ca-label">Me contacter par</label>
                <div className="ca-type-row">
                  <button
                    type="button"
                    onClick={() => setContactType("email")}
                    className={`ca-type-btn ${contactType === "email" ? "active" : ""}`}
                  >
                    <Mail size={15} /> Email
                  </button>
                  <button
                    type="button"
                    onClick={() => setContactType("whatsapp")}
                    className={`ca-type-btn ${contactType === "whatsapp" ? "active" : ""}`}
                  >
                    <Phone size={15} /> WhatsApp
                  </button>
                </div>
              </div>
            )}

            {/* Contact */}
            <div className="ca-field">
              <label className="ca-label">
                {mode === "register"
                  ? contactType === "email"
                    ? "Adresse email"
                    : "Numéro WhatsApp"
                  : "Email ou WhatsApp"}
              </label>
              <div className="ca-input-wrap">
                {contactType === "email" || mode === "login" ? (
                  <Mail size={15} className="ca-input-icon" />
                ) : (
                  <Phone size={15} className="ca-input-icon" />
                )}
                <input
                  type={contactType === "email" || mode === "login" ? "text" : "tel"}
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  placeholder={
                    contactType === "email" || mode === "login"
                      ? "marie@exemple.com"
                      : "+33 6 12 34 56 78"
                  }
                  className="ca-input"
                  required
                  data-testid="customer-contact"
                />
              </div>
            </div>

            {/* Password */}
            <div className="ca-field">
              <label className="ca-label">Mot de passe</label>
              <div className="ca-input-wrap">
                <Lock size={15} className="ca-input-icon" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="ca-input ca-input-pwd"
                  required
                  minLength={4}
                  data-testid="customer-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="ca-pwd-toggle"
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {mode === "register" && (
                <span className="ca-hint">4 caractères minimum</span>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="ca-submit"
              data-testid="customer-submit"
            >
              {loading ? (
                <Loader2 size={16} className="ca-spin" />
              ) : mode === "register" ? (
                "Créer mon espace"
              ) : (
                "Me connecter"
              )}
            </button>
          </form>

          {/* Benefits (register only) */}
          {mode === "register" && (
            <div className="ca-benefits">
              <p className="ca-benefits-title">Avec mon espace, je peux :</p>
              <div className="ca-benefits-list">
                <div className="ca-benefit">
                  <Heart size={13} className="ca-benefit-icon" />
                  <span>Sauvegarder mes articles favoris</span>
                </div>
                <div className="ca-benefit">
                  <MessageCircle size={13} className="ca-benefit-icon" />
                  <span>Échanger avec la boutique</span>
                </div>
                <div className="ca-benefit">
                  <ShoppingBag size={13} className="ca-benefit-icon" />
                  <span>Suivre mes commandes</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .ca-root {
          min-height: 100vh;
          background: #0A0A0A;
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          position: relative;
          overflow-x: hidden;
        }
        .ca-root::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px);
          background-size: 64px 64px;
          pointer-events: none;
          z-index: 0;
        }

        .ca-back {
          position: fixed;
          top: 20px;
          left: 20px;
          z-index: 200;
          color: #6B7280;
          font-size: 13px;
          text-decoration: none;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: color 150ms;
          font-family: 'Inter', system-ui, sans-serif;
        }
        .ca-back:hover { color: #9CA3AF; }

        .ca-wrapper {
          display: flex;
          justify-content: center;
          padding: 80px 16px 40px;
          position: relative;
          z-index: 1;
        }

        .ca-card {
          position: relative;
          width: 100%;
          max-width: 400px;
          background: #111111;
          border: 1px solid #2A2A2A;
          border-radius: 16px;
          box-shadow: 0 24px 60px rgba(0,0,0,0.5);
          padding: 28px 24px 24px;
          overflow: hidden;
        }
        .ca-accent {
          position: absolute;
          top: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 60%;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255,69,0,0.4), transparent);
        }

        .ca-icon-wrap {
          width: 48px;
          height: 48px;
          background: rgba(255,69,0,0.1);
          border: 1px solid rgba(255,69,0,0.2);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 16px;
          color: #FF4500;
        }

        .ca-title {
          font-size: 20px;
          font-weight: 600;
          color: #FFFFFF;
          text-align: center;
          margin: 0 0 4px;
          letter-spacing: -0.02em;
        }
        .ca-subtitle {
          font-size: 13px;
          color: #6B7280;
          text-align: center;
          margin: 0 0 24px;
          line-height: 1.5;
        }

        /* Toggle */
        .ca-toggle {
          display: flex;
          background: #0A0A0A;
          border: 1px solid #2A2A2A;
          border-radius: 10px;
          padding: 3px;
          margin-bottom: 24px;
        }
        .ca-toggle-btn {
          flex: 1;
          padding: 9px 0;
          border: none;
          border-radius: 8px;
          background: transparent;
          color: #6B7280;
          font-size: 13px;
          font-weight: 500;
          font-family: 'Inter', system-ui, sans-serif;
          cursor: pointer;
          transition: all 200ms;
        }
        .ca-toggle-btn.active {
          background: #1A1A1A;
          color: #FFFFFF;
          box-shadow: 0 1px 4px rgba(0,0,0,0.3);
        }

        /* Form */
        .ca-form {
          display: flex;
          flex-direction: column;
          gap: 18px;
        }

        .ca-field {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .ca-label {
          font-size: 12px;
          font-weight: 500;
          color: #9CA3AF;
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }

        .ca-input-wrap {
          position: relative;
          display: flex;
          align-items: center;
        }
        .ca-input-icon {
          position: absolute;
          left: 14px;
          color: #4B5563;
          pointer-events: none;
          flex-shrink: 0;
        }
        .ca-input {
          width: 100%;
          box-sizing: border-box;
          background: #0A0A0A;
          border: 1px solid #2A2A2A;
          border-radius: 10px;
          padding: 12px 14px 12px 40px;
          font-family: 'Inter', system-ui, sans-serif;
          font-size: 14px;
          color: #FFFFFF;
          outline: none;
          transition: border-color 150ms, box-shadow 150ms;
          -webkit-appearance: none;
        }
        .ca-input::placeholder { color: #6B7280; }
        .ca-input:focus {
          border-color: #FF4500;
          box-shadow: 0 0 0 3px rgba(255,69,0,0.15);
        }
        .ca-input-pwd { padding-right: 44px; }

        .ca-pwd-toggle {
          position: absolute;
          right: 14px;
          background: none;
          border: none;
          color: #4B5563;
          cursor: pointer;
          padding: 0;
          display: flex;
          transition: color 150ms;
        }
        .ca-pwd-toggle:hover { color: #9CA3AF; }

        .ca-hint {
          font-size: 11px;
          color: rgba(255,255,255,0.25);
          margin-top: 2px;
        }

        /* Contact type buttons */
        .ca-type-row {
          display: flex;
          gap: 8px;
        }
        .ca-type-btn {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 10px;
          border-radius: 10px;
          border: 1px solid #2A2A2A;
          background: #1A1A1A;
          color: #6B7280;
          font-size: 13px;
          font-weight: 500;
          font-family: 'Inter', system-ui, sans-serif;
          cursor: pointer;
          transition: all 200ms;
        }
        .ca-type-btn.active {
          border-color: rgba(255,69,0,0.35);
          background: rgba(255,69,0,0.06);
          color: #FF4500;
        }

        /* Submit */
        .ca-submit {
          width: 100%;
          background: #FFFFFF;
          color: #0A0A0A;
          border: none;
          border-radius: 10px;
          padding: 13px;
          font-size: 14px;
          font-weight: 600;
          font-family: 'Inter', system-ui, sans-serif;
          cursor: pointer;
          transition: opacity 150ms, transform 150ms;
          letter-spacing: -0.01em;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-top: 4px;
        }
        .ca-submit:hover { opacity: 0.88; }
        .ca-submit:active { transform: scale(0.98); }
        .ca-submit:disabled { opacity: 0.35; cursor: not-allowed; }

        @keyframes caSpin { to { transform: rotate(360deg); } }
        .ca-spin { animation: caSpin 0.8s linear infinite; }

        /* Benefits */
        .ca-benefits {
          margin-top: 20px;
          padding: 16px;
          background: #1A1A1A;
          border: 1px solid #2A2A2A;
          border-radius: 12px;
        }
        .ca-benefits-title {
          font-size: 13px;
          font-weight: 600;
          color: rgba(255,255,255,0.85);
          margin: 0 0 12px;
        }
        .ca-benefits-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .ca-benefit {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 12px;
          color: #9CA3AF;
        }
        .ca-benefit-icon { color: #FF4500; flex-shrink: 0; }

        @media (max-width: 480px) {
          .ca-wrapper { padding: 70px 16px 32px; }
          .ca-card { padding: 24px 18px 20px; }
        }
      `}</style>
    </div>
  );
}
